import React, { useState, useContext } from 'react'
import { Button, Card, Divider, Flex, Popover, Switch, Tag } from 'antd'
import { t } from 'i18next'
import dayjs from 'dayjs'
import DOMPurify from 'dompurify'
import EventTag from '../Admin/TagsPage/EventTag'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'
import LoginContext from '../Admin/LoginContext'
import BoxContext from '../Admin/BoxPage/BoxContext'
import { formatPhoneNumber } from 'react-phone-number-input'

export function SafeHtml ({ html, as: Tag = 'p', ...rest }) {
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html ?? '') }} />
}

export const PHONE_NUMBER = '04 48 24 01 28'
export const PHONE_HREF = 'tel:+33448240128'

export function onlyUnique (value, index, array) {
  return array.indexOf(value) === index
}

export function screenSize () {
  const screens = useBreakpoint()
  if (screens.xs) {
    return 'Mobile'
  } else if (screens.sm && !screens.lg) {
    return 'Tablet'
  } else {
    return 'Computer'
  }
}

export const ROLES = ['cleaning', 'maintenance', 'municipacility', 'hospital', 'territory_responsable', 'emergency', 'other']
export const APPOINTMENT_TYPES = ['PatientAppointment', 'CleaningAppointment', 'MaintenanceAppointment', 'WorkAppointment']
export const INTERVENER_TAGS = ['electricity', 'terminal_test', 'support']

export function stripHtml (html) {
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

export function appointmentTitle (appointment) {
  if (appointment.type === 'CleaningAppointment') {
    return `[Nettoyage] ${appointment.name}`
  } else if (appointment.type === 'MaintenanceAppointment') {
    return `[Maintenance] ${appointment.name}`
  } else if (appointment.type === 'WorkAppointment') {
    return `[Animation] ${appointment.name}`
  } else {
    return `${appointment.name}`
  }
}

function displayedTagsHierarchy (displayedTags, displayTag = false) {
  const hierarchy = {}
  displayedTags.forEach((tag) => {
    if (hierarchy[tag.family.business_area.id] === undefined) {
      hierarchy[tag.family.business_area.id] = { businessArea: tag.family.business_area, families: [], tags: [] }
    }
    if (!hierarchy[tag.family.business_area.id].families.map((e) => e.id).includes(tag.family.id)) {
      hierarchy[tag.family.business_area.id].families.push(tag.family)
    }
    if (displayTag) {
      hierarchy[tag.family.business_area.id].tags.push(tag)
    }
  })
  return hierarchy
}

function hierarchyToHtmlTag (hierarchy) {
  const hierarchyFormat = []
  for (const key in hierarchy) {
    const businessArea = hierarchy[key].businessArea
    const families = hierarchy[key].families
    const tags = hierarchy[key].tags
    let hierarchyTags = families.map((family) => (<EventTag key={family.id} tag={family} type='family' />))
    if (tags.length !== 0) {
      hierarchyTags = hierarchyTags.concat(tags.map((tag) => (<EventTag key={tag.id} tag={tag} type='tag' />))).flat()
    }
    hierarchyTags.unshift(<EventTag key={businessArea.id} tag={businessArea} type='businessArea' />)
    hierarchyFormat.push(hierarchyTags)
  }
  return hierarchyFormat
}

export function displayHierarchy (displayedTags, displayTag = false) {
  if (displayedTags?.length === 0) {
    return t('Admin.Event.noTags')
  } else {
    return hierarchyToHtmlTag(displayedTagsHierarchy(displayedTags, displayTag)).map((tags, index) => (
      <div key={`div-${index}`}>
        {tags}
      </div>
    ))
  }
}

export function displayBoxEmptyFields (fields) {
  return fields.map((field) => (<div key={field}>- {t(`Admin.BoxList.table.${field}`)}</div>))
}

export function Logs ({ entries, title }) {
  const entryRender = (entry) => (
    <Flex align='center' gap='middle' key={entry.id}>
      <Tag color={entry.color} variant='solid'>
        <span style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>
          {dayjs(entry.created_at).format('HH:mm:ss')}
        </span>
      </Tag>
      {entry.message}
    </Flex>
  )

  const header = <Flex align='center' gap='middle' vertical style={{ padding: '1em 0' }}>
    <div style={{ fontSize: '22px' }}>{t('Admin.Common.Logs.header')}</div>
    {title}
  </Flex>

  const elements = entries.flatMap((entry, index) => [<Divider key={`divider-${index}`} />, entryRender(entry)]).slice(1)

  return (
    <Card title={header} style={{ maxHeight: '45em', overflowY: 'scroll' }} >
      <Flex vertical>
        {elements}
      </Flex>
    </Card>
  )
}

export function AdminLogs ({ entries, title }) {
  const [environmentalLogs, setEnvironmentalLogs] = useState(true)
  const [teleconsultingLogs, setTeleconsultingLogs] = useState(true)

  const filter = (entry, environmentalLogs, teleconsultingLogs) => {
    if (environmentalLogs && teleconsultingLogs) {
      return entry.type === 'log' || entry.type === 'teleconsulting_log'
    } else if (environmentalLogs || teleconsultingLogs) {
      return environmentalLogs ? entry.type === 'log' : entry.type === 'teleconsulting_log'
    } else {
      return false
    }
  }

  const entryRender = (entry) => (
    <Flex align='center' gap='middle' key={entry.id}>
      <Tag color={entry.color} variant='solid'>
        <span style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>
          {dayjs(entry.created_at).format('HH:mm:ss')}
        </span>
      </Tag>
      {entry.message}
    </Flex>
  )

  const header = <Flex align='center' gap='middle' vertical style={{ padding: '1em 0' }}>
    <div style={{ fontSize: '22px' }}>{t('Admin.Common.Logs.header')}</div>
    {title}
    <Flex gap='2em'>
      <Switch checkedChildren='Environnement' unCheckedChildren='Environnement' checked={environmentalLogs} onChange={(value) => setEnvironmentalLogs(value)} />
      <Switch checkedChildren='Téléconsultation' unCheckedChildren='Téléconsultation' checked={teleconsultingLogs} onChange={(value) => setTeleconsultingLogs(value)} />
    </Flex>
  </Flex>
  const filteredEntries = entries.filter(e => filter(e, environmentalLogs, teleconsultingLogs))
  const elements = filteredEntries.flatMap((entry, index) => [<Divider key={`divider-${index}`} />, entryRender(entry)]).slice(1)
  console.log(entries)
  console.log(elements)

  return (
    <Card title={header} style={{ maxHeight: '45em', overflowY: 'scroll' }} >
      <Flex vertical>
        {elements}
      </Flex>
    </Card>
  )
}

export function ActionButton (props) {
  const { user } = useContext(LoginContext)

  return (
    <Button {...props} disabled={props.disabled || user.roles.includes('reader')}>
      {props.children}
    </Button>
  )
}

export function intervenerOptionsForAppointment () {
  const { box } = useContext(BoxContext)
  const options = [
    {
      value: 'Internal',
      label: t('Admin.Intervener.attributes.internal'),
      selectable: false,
      children: box.internal_interveners.map(i => ({ value: i.id, label: `${i.name} [${i.job || ''}]` }))
    },
    {
      value: 'External',
      label: t('Admin.Intervener.attributes.external'),
      selectable: false,
      children: box.interveners.filter(i => i.external).map(i => ({ value: i.id, label: `${i.name} [${i.job || ''}]` }))
    }
  ]
  return options
}

export const displayCommunication = (communication) => {
  const value = communication.field_type === 'phone' ? formatPhoneNumber(`+${communication.value}`) : communication.value
  return (
    <Card styles={{ body: { padding: 0, textAlign: 'center' } }}>
      {
        communication.description &&
        <Popover content={communication.description}>
          {value}
        </Popover>
      }
      {
        !communication.description && value
      }
    </Card>
  )
}
